/**
 * Lazy image with blur-up placeholder.
 * Shows a pulsing muted background while the image loads, then fades
 * in the real image once decoded. Handles already-cached images gracefully.
 */
import { useState, useCallback, useRef, forwardRef } from "react";
import { cn } from "@/lib/utils";

interface BlurImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
}

const BlurImage = forwardRef<HTMLDivElement, BlurImageProps>(
  ({ src, alt, className, ...props }, ref) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    const imgCallback = useCallback((node: HTMLImageElement | null) => {
      if (node && node.complete && node.naturalWidth > 0) {
        setIsLoaded(true);
      }
    }, []);

    return (
      <div ref={ref} className="relative overflow-hidden h-full w-full">
        {!isLoaded && (
          <div
            className="absolute inset-0 bg-muted animate-pulse"
            aria-hidden="true"
          />
        )}
        <img
          ref={imgCallback}
          src={src}
          alt={alt}
          className={cn(
            "transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0",
            className
          )}
          onLoad={() => setIsLoaded(true)}
          {...props}
        />
      </div>
    );
  }
);

BlurImage.displayName = "BlurImage";

export default BlurImage;
